import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Stack,
  Button,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  useTheme
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { isAfter, isBefore } from 'date-fns';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChartSettings from './ChartSettings';

const SettingsDialog = ({
  open,
  onClose,
  dateRange,
  onDateRangeChange,
  splitCount,
  onSplitCountChange,
  statsOpenHour,
  onStatsOpenHourChange,
  statsCloseHour,
  onStatsCloseHourChange,
  operatingHoursDisplay,
  isLoading,
  onRefresh,
  getConfigOptionsWithFallback,
  getConfigValueWithDefault,
  isQuickSelectActive,
  onQuickRange
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 32px 64px rgba(0, 0, 0, 0.08)',
          background: 'linear-gradient(to bottom, #ffffff, #fafafa)',
          overflow: 'visible'
        }
      }}
    >
      {/* Clean Header */}
      <DialogTitle sx={{ 
        p: 0,
        position: 'relative'
      }}>
        <Box sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'white',
          p: 3,
          borderRadius: '16px 16px 0 0',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Pattern */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 200,
            height: 200,
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            opacity: 0.3
          }} />
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <SettingsIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Settings
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 400 }}>
                  Configure time periods and chart appearance
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={onClose}
              sx={{ 
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>
          
          {/* Time Configuration Section */}
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.text.primary,
                mb: 2,
                fontSize: '1.1rem'
              }}
            >
              Time Configuration
            </Typography>
            
            <Box sx={{ 
              background: 'white',
              borderRadius: 2,
              p: 3,
              border: `1px solid ${theme.palette.grey[100]}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <Grid container spacing={4}>
                {/* Date Range Pickers */}
                <Grid item xs={12} lg={5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Date Range
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Stack spacing={2.5}>
                      <DateTimePicker
                        label="Start Date & Time"
                        value={dateRange[0]}
                        onChange={(newDate) => {
                          if (newDate && isBefore(newDate, dateRange[1])) {
                            onDateRangeChange([newDate, dateRange[1]]);
                          }
                        }}
                        slotProps={{
                          textField: { 
                            fullWidth: true,
                            sx: { 
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: '#fafafa',
                                border: `1px solid ${theme.palette.grey[200]}`,
                                '&:hover': {
                                  backgroundColor: '#f5f5f5',
                                  borderColor: theme.palette.primary.main
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'white',
                                  boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`
                                }
                              }
                            } 
                          }
                        }}
                        format="MM/dd/yyyy hh:mm a"
                        views={['year', 'month', 'day', 'hours', 'minutes']}
                      />
                      <DateTimePicker
                        label="End Date & Time"
                        value={dateRange[1]}
                        onChange={(newDate) => {
                          if (newDate && isAfter(newDate, dateRange[0])) {
                            onDateRangeChange([dateRange[0], newDate]);
                          }
                        }}
                        slotProps={{
                          textField: { 
                            fullWidth: true,
                            sx: { 
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: '#fafafa',
                                border: `1px solid ${theme.palette.grey[200]}`,
                                '&:hover': {
                                  backgroundColor: '#f5f5f5',
                                  borderColor: theme.palette.primary.main
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'white',
                                  boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`
                                }
                              }
                            } 
                          }
                        }}
                        format="MM/dd/yyyy hh:mm a"
                        views={['year', 'month', 'day', 'hours', 'minutes']}
                      />
                    </Stack>
                  </LocalizationProvider>
                </Grid>
                
                {/* Quick Select */}
                <Grid item xs={12} lg={3}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Quick Select
                  </Typography>
                  <Stack spacing={1}>
                    {getConfigOptionsWithFallback('settings_default_date_range_days').map((option) => {
                      const getDaysFromOption = (option) => {
                        switch (option) {
                          case 'day': return 1;
                          case 'week': return 7;
                          case 'month': return 30;
                          default: return 30;
                        }
                      };
                      
                      const getDisplayLabel = (option) => {
                        switch (option) {
                          case 'day': return 'Last 24 Hours';
                          case 'week': return 'Last Week';
                          case 'month': return 'Last Month';
                          default: return option.charAt(0).toUpperCase() + option.slice(1);
                        }
                      };
                      
                      const isActive = isQuickSelectActive(option);
                      
                      return (
                        <Button 
                          key={option}
                          variant={isActive ? "contained" : "outlined"} 
                          size="small"
                          fullWidth
                          onClick={() => onQuickRange(getDaysFromOption(option))}
                          sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            py: 1,
                            justifyContent: 'flex-start',
                            ...(isActive ? {
                              backgroundColor: theme.palette.primary.main,
                              color: 'white',
                              boxShadow: 'none',
                              '&:hover': {
                                backgroundColor: theme.palette.primary.dark
                              }
                            } : {
                              borderColor: theme.palette.grey[200],
                              color: theme.palette.text.primary,
                              backgroundColor: 'transparent',
                              '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                borderColor: theme.palette.primary.main
                              }
                            })
                          }}
                        >
                          {getDisplayLabel(option)}
                        </Button>
                      );
                    })}
                  </Stack>
                </Grid>

                {/* Operating Hours */}
                <Grid item xs={12} lg={4}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Operating Hours
                  </Typography>
                  
                  {/* Site Default */}
                  <Box sx={{ 
                    p: 2,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[100]}`,
                    mb: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="caption" sx={{ 
                      fontWeight: 600, 
                      color: theme.palette.text.secondary,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5
                    }}>
                      Site Default
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      mt: 0.5
                    }}>
                      {operatingHoursDisplay}
                    </Typography>
                  </Box>
                  
                  {/* Custom Hours */}
                  <Typography variant="caption" sx={{ mb: 1.5, fontWeight: 600, display: 'block' }}>
                    Custom Analysis day interval Hours
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 500, display: 'block', fontSize: '0.7rem' }}>
                        Start
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={statsOpenHour}
                          onChange={(e) => onStatsOpenHourChange(e.target.value)}
                          sx={{ 
                            borderRadius: 1.5,
                            backgroundColor: '#fafafa',
                            fontSize: '0.8rem',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.grey[200]
                            }
                          }}
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <MenuItem key={i} value={i} sx={{ fontSize: '0.8rem' }}>
                              {i.toString().padStart(2, '0')}:00
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 500, display: 'block', fontSize: '0.7rem' }}>
                        End
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={statsCloseHour}
                          onChange={(e) => onStatsCloseHourChange(e.target.value)}
                          sx={{ 
                            borderRadius: 1.5,
                            backgroundColor: '#fafafa',
                            fontSize: '0.8rem',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.grey[200]
                            }
                          }}
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <MenuItem key={i} value={i} sx={{ fontSize: '0.8rem' }}>
                              {i.toString().padStart(2, '0')}:00
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Chart Configuration */}
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.text.primary,
                mb: 2,
                fontSize: '1.1rem'
              }}
            >
              Chart Configuration
            </Typography>
            
            <Grid container spacing={3}>
              {/* Time Periods */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  background: 'white',
                  borderRadius: 3,
                  p: 3,
                  border: `1px solid ${theme.palette.grey[100]}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Time Periods
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={splitCount}
                      onChange={(e) => onSplitCountChange(e.target.value)}
                      sx={{
                        borderRadius: 2,
                        backgroundColor: '#fafafa',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.grey[200]
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                          borderWidth: 1
                        }
                      }}
                    >
                      {getConfigOptionsWithFallback('settings_default_time_periods').map((periods) => (
                        <MenuItem key={periods} value={periods}>
                          {periods} Time Periods
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              {/* Chart Colors */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  background: 'white',
                  borderRadius: 3,
                  p: 3,
                  border: `1px solid ${theme.palette.grey[100]}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Chart Colors
                  </Typography>
                  <ChartSettings 
                    onPaletteChange={(chartType, paletteId) => {
                      console.log(`Palette changed for ${chartType} charts to: ${paletteId}`);
                    }}
                    defaultBarPalette={getConfigValueWithDefault('settings_chart_default_bar_color_palette', 'pastel')}
                    defaultPiePalette={getConfigValueWithDefault('settings_chart_default_pie_color_palette', 'colorful')}
                    compact={true}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      {/* Clean Footer */}
      <DialogActions sx={{ 
        p: 4, 
        pt: 0,
        gap: 2
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.2,
            borderColor: theme.palette.grey[300],
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.grey[400],
              backgroundColor: theme.palette.grey[50]
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          startIcon={<RefreshIcon />}
          variant="contained"
          onClick={() => {
            onRefresh();
            onClose();
          }}
          disabled={isLoading}
          sx={{ 
            px: 4,
            py: 1.2,
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              boxShadow: `0 8px 24px ${theme.palette.primary.main}40`
            },
            '&:disabled': {
              background: theme.palette.grey[300]
            }
          }}
        >
          Apply Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;
